import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ReportCardView } from '../../lib/report_cards';
import { CancelDaycareDrawer } from '../components/CancelDaycareDrawer';
import { CancelGroomingDrawer } from '../components/CancelGroomingDrawer';
import { MessagesThread } from '../components/MessagesThread';
import { OnMyWayDrawer } from '../components/OnMyWayDrawer';
import { PayBookingPanel } from '../components/PayBookingPanel';
import { PhotoTimeline } from '../components/PhotoTimeline';
import { ReportCardList } from '../components/ReportCardList';
import { RequestRefundDrawer } from '../components/RequestRefundDrawer';
import { RescheduleDrawer } from '../components/RescheduleDrawer';
import { StatusPill } from '../components/StatusPill';
import { customerApi } from '../customer_api';
import { formatDate, formatDateRange, formatMoney } from '../lib/format';
import { getStripePublishableKey } from '../lib/stripe_env';
import { useSpaceNoun } from '../lib/useSpaceNoun';
import { useStayRealtime } from '../lib/useStayRealtime';
import type { CustomerPayment } from './Payments';

interface BookingBase {
  id: string;
  type: 'boarding' | 'daycare' | 'grooming' | 'service';
  status: string;
  totalMinor: number;
  createdAt: string;
  updatedAt: string;
}

interface BoardingStay {
  id: string;
  checkIn: string;
  checkOut: string;
  runId: string | null;
  /** Joined by the API — null when no run is assigned. */
  runName?: string | null;
}

interface DaycareVisit {
  id: string;
  date: string;
  dropOffAt: string | null;
  pickupAt: string | null;
}

interface GroomingAppointment {
  id: string;
  startAt: string;
  durationMin: number | null;
  groomerId: string | null;
  /** Joined by the API — null when groomer has no display name or is unassigned. */
  groomerName?: string | null;
}

interface CustomerBelonging {
  id: string;
  description: string;
  photoR2Key: string | null;
  recordedAt: string;
  returnedAt: string | null;
}

/** Migration 0103 — extra nights charged by the kennel's overstay policy. */
interface CustomerOverstayLineItem {
  id: string;
  nightDate: string;
  priceMinor: number;
  currency: string;
}

type BookingDetailResponse = {
  belongings?: CustomerBelonging[];
  overstayLineItems?: CustomerOverstayLineItem[];
  petName?: string | null;
  /** Migration 0102 — which household login placed the booking (null = staff / unknown). */
  bookedBy?: { customerUserId: string; name: string | null; isSelf: boolean } | null;
} & (
  | { booking: BookingBase; type: 'boarding'; stay: BoardingStay }
  | { booking: BookingBase; type: 'daycare'; visit: DaycareVisit }
  | { booking: BookingBase; type: 'grooming'; appointment: GroomingAppointment }
  | { booking: BookingBase; type: 'service' }
);

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem 1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '1rem',
        padding: '0.3rem 0',
        fontSize: '0.9rem',
      }}
    >
      <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

import type React from 'react';

/**
 * Statuses from which a customer can request a refund (i.e. the booking
 * has not yet been fully resolved). The spec says: pending, confirmed,
 * confirmed_deposit, paid — NOT cancelled, completed, checked_in, checked_out.
 * The BookingStatus type is narrower (Phase 2) so we use a string check.
 */
function isCancellable(status: string): boolean {
  return (
    status !== 'cancelled' &&
    status !== 'completed' &&
    status !== 'checked_in' &&
    status !== 'checked_out' &&
    status !== 'no_show'
  );
}

/**
 * Daycare-specific self-serve cancellation (sub-spec 04 Plan 5 M4). The
 * customer can cancel pending / confirmed daycare visits subject to the
 * tenant's cancellation policy; the staff-side refund-request flow is the
 * fallback for boarding / grooming and for already-checked-in visits.
 */
function isDaycareCancellable(status: string): boolean {
  return status === 'pending' || status === 'confirmed';
}

/**
 * Grooming-specific self-serve cancellation (sub-spec 05 Plan 5 M2). Same
 * gating as daycare — customer can only self-cancel pending / confirmed
 * appointments. Past-start guard lives server-side.
 */
function isGroomingCancellable(status: string, startAt: string | null | undefined): boolean {
  if (status !== 'pending' && status !== 'confirmed') return false;
  if (!startAt) return true;
  return startAt > new Date().toISOString();
}

export function BookingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const spaceNoun = useSpaceNoun();
  const [data, setData] = useState<BookingDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<CustomerPayment[] | null>(null);
  const [showRefundDrawer, setShowRefundDrawer] = useState(false);
  const [refundSubmittedBanner, setRefundSubmittedBanner] = useState(false);
  const [showRescheduleDrawer, setShowRescheduleDrawer] = useState(false);
  const [rescheduledBanner, setRescheduledBanner] = useState(false);
  // Migration 0079 — "I'm on my way" choreography.
  const [showOnMyWayDrawer, setShowOnMyWayDrawer] = useState(false);
  const [onMyWayBanner, setOnMyWayBanner] = useState(false);
  const [showDaycareCancelDrawer, setShowDaycareCancelDrawer] = useState(false);
  const [daycareCancelBanner, setDaycareCancelBanner] = useState<{
    refundMinor: number;
    refundSource: string;
  } | null>(null);
  const [showGroomingCancelDrawer, setShowGroomingCancelDrawer] = useState(false);
  const [groomingCancelBanner, setGroomingCancelBanner] = useState<{
    refundMinor: number;
    refundSource: string;
  } | null>(null);

  // Sub-spec 01c — realtime nonce drives MessagesThread re-fetch on
  // BOOKING_MESSAGE_POSTED events for this booking.
  const { nonce: realtimeNonce } = useStayRealtime({ bookingId: id ?? '', enabled: !!id });

  // Pet report cards (migration 0093) — re-fetched on every realtime bump so
  // a REPORT_CARD_SENT event surfaces the new card without a reload.
  const [reportCards, setReportCards] = useState<ReportCardView[]>([]);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    customerApi<{ reportCards: ReportCardView[] }>(`/api/customer/bookings/${id}/report-cards`)
      .then((body) => {
        if (!cancelled) setReportCards(body.reportCards ?? []);
      })
      .catch(() => {
        /* non-fatal — the section simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [id, realtimeNonce]);

  const fetchBooking = useCallback(() => {
    if (!id) return;
    customerApi<BookingDetailResponse>(`/api/customer/bookings/${id}`)
      .then(setData)
      .catch(() => setError('Could not load booking. Please try again.'));
  }, [id]);

  const fetchPayments = useCallback(() => {
    if (!id) return;
    customerApi<{ payments: CustomerPayment[] }>('/api/customer/payments')
      .then((d) => setPayments(d.payments))
      .catch(() => setPayments([]));
  }, [id]);

  useEffect(() => {
    fetchBooking();
    fetchPayments();
  }, [fetchBooking, fetchPayments]);

  const handlePaid = useCallback(() => {
    // Refetch both: the booking status moved (pending_payment →
    // confirmed_deposit or paid) and the ledger has a new row.
    fetchBooking();
    fetchPayments();
  }, [fetchBooking, fetchPayments]);

  const handleRefundSuccess = useCallback(() => {
    setShowRefundDrawer(false);
    setRefundSubmittedBanner(true);
    fetchBooking();
    fetchPayments();
  }, [fetchBooking, fetchPayments]);

  const handleDaycareCancelSuccess = useCallback(
    (result: { refundMinor: number; refundSource: string }) => {
      setShowDaycareCancelDrawer(false);
      setDaycareCancelBanner(result);
      fetchBooking();
      fetchPayments();
    },
    [fetchBooking, fetchPayments],
  );

  const handleGroomingCancelSuccess = useCallback(
    (result: { refundMinor: number; refundSource: string }) => {
      setShowGroomingCancelDrawer(false);
      setGroomingCancelBanner(result);
      fetchBooking();
      fetchPayments();
    },
    [fetchBooking, fetchPayments],
  );

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/account/bookings"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.85rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            textDecoration: 'none',
            marginBottom: '1.5rem',
          }}
        >
          <ArrowLeft size={14} />
          Bookings
        </Link>

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}

        {!error && data === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {data !== null &&
          (() => {
            const { booking } = data;
            return (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <h1
                    style={{
                      fontSize: '1.75rem',
                      fontWeight: 500,
                      letterSpacing: '-0.02em',
                      margin: 0,
                      textTransform: 'capitalize',
                    }}
                  >
                    {booking.type}
                  </h1>
                  <StatusPill status={booking.status} />
                </div>
                {/* Migration 0095 — request-and-approve mode: what happens next */}
                {booking.status === 'requested' && (
                  <div
                    role="status"
                    data-testid="booking-requested-notice"
                    style={{
                      marginTop: '1rem',
                      padding: '0.85rem 1rem',
                      borderRadius: '10px',
                      background: 'oklch(0.68 0.12 78 / 0.12)',
                      color: 'var(--warning-foreground, oklch(0.22 0.04 75))',
                      fontSize: '0.9rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Awaiting approval.</strong> We&rsquo;ve received your request and the
                    team will review it shortly. Your space is held in the meantime and nothing is
                    charged until it&rsquo;s approved &mdash; we&rsquo;ll email you as soon as
                    there&rsquo;s an update.
                  </div>
                )}
                {data.petName && (
                  <p
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '0.95rem',
                      color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                    }}
                  >
                    {data.petName}
                  </p>
                )}

                <Section title="Overview">
                  <Row label="Booked on" value={formatDate(booking.createdAt)} />
                  {data.bookedBy && !data.bookedBy.isSelf ? (
                    <Row
                      label="Booked by"
                      value={data.bookedBy.name ?? 'Another member of your household'}
                    />
                  ) : null}
                  <Row
                    label="Total"
                    value={<span className="num">{formatMoney(booking.totalMinor)}</span>}
                  />
                </Section>

                {data.type === 'boarding' && (
                  <Section title="Stay details">
                    <Row
                      label="Dates"
                      value={
                        <span className="num">
                          {formatDateRange(data.stay.checkIn, data.stay.checkOut)}
                        </span>
                      }
                    />
                    {(data.stay.runName ?? data.stay.runId) && (
                      <Row
                        label={spaceNoun.Singular}
                        value={data.stay.runName ?? data.stay.runId ?? ''}
                      />
                    )}
                  </Section>
                )}

                {data.type === 'daycare' && (
                  <Section title="Visit details">
                    <Row
                      label="Date"
                      value={<span className="num">{formatDate(data.visit.date)}</span>}
                    />
                    {data.visit.dropOffAt && (
                      <Row
                        label="Drop-off"
                        value={<span className="num">{data.visit.dropOffAt}</span>}
                      />
                    )}
                    {data.visit.pickupAt && (
                      <Row
                        label="Pick-up"
                        value={<span className="num">{data.visit.pickupAt}</span>}
                      />
                    )}
                  </Section>
                )}

                {data.type === 'grooming' && (
                  <Section title="Appointment details">
                    <Row
                      label="Start"
                      value={<span className="num">{formatDate(data.appointment.startAt)}</span>}
                    />
                    {data.appointment.durationMin !== null &&
                      data.appointment.durationMin !== undefined && (
                        <Row
                          label="Duration"
                          value={<span className="num">{data.appointment.durationMin} min</span>}
                        />
                      )}
                    {(data.appointment.groomerName ?? data.appointment.groomerId) && (
                      <Row
                        label="Groomer"
                        value={data.appointment.groomerName ?? data.appointment.groomerId ?? ''}
                      />
                    )}
                  </Section>
                )}

                {/* Migration 0103 — extra nights added because the pet stayed
                    past the scheduled checkout. Shown with the night each
                    charge covers so the total is never a mystery. */}
                {data.overstayLineItems && data.overstayLineItems.length > 0 && (
                  <Section title="Extra nights">
                    {data.overstayLineItems.map((item) => (
                      <Row
                        key={item.id}
                        label={
                          <span className="num" data-testid="customer-overstay-line-item">
                            Night of {formatDate(item.nightDate)}
                          </span>
                        }
                        value={
                          <span className="num">{formatMoney(item.priceMinor, item.currency)}</span>
                        }
                      />
                    ))}
                  </Section>
                )}

                {/* Sub-spec 06 Plan 5 — read-only belongings list. Staff
                    manages add/edit/return; customer just sees what's been
                    recorded.
                */}
                {data.belongings && data.belongings.length > 0 && (
                  <Section title="Belongings">
                    {data.belongings.map((item) => (
                      <Row
                        key={item.id}
                        label={
                          <span
                            style={
                              item.returnedAt
                                ? { textDecoration: 'line-through', opacity: 0.6 }
                                : undefined
                            }
                          >
                            {item.description}
                          </span>
                        }
                        value={
                          item.returnedAt ? (
                            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Returned</span>
                          ) : (
                            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>In our care</span>
                          )
                        }
                      />
                    ))}
                  </Section>
                )}

                {/*
                PayBookingPanel renders only when the booking is in
                `pending_payment` — that's the only status the POST /pay
                handler accepts. For `confirmed_deposit`, the balance is
                auto-charged off-session by the daily cron (Plan 3 M5).
              */}
                {(() => {
                  const pk = getStripePublishableKey();
                  if (booking.status !== 'pending_payment' || !pk) return null;
                  return (
                    <PayBookingPanel
                      bookingId={booking.id}
                      publishableKey={pk}
                      onPaid={handlePaid}
                    />
                  );
                })()}

                {/* Daycare cancellation success banner */}
                {daycareCancelBanner && (
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      border: '1px solid oklch(0.55 0.12 150 / 0.30)',
                      background: 'oklch(0.55 0.12 150 / 0.08)',
                      color: 'oklch(0.36 0.12 150)',
                      fontSize: '0.9rem',
                    }}
                    role="status"
                    data-testid="daycare-cancelled-banner"
                  >
                    {daycareCancelBanner.refundSource === 'account_credit'
                      ? `Visit cancelled. ${formatMoney(daycareCancelBanner.refundMinor)} has been added to your account credit.`
                      : 'Visit cancelled.'}
                  </div>
                )}

                {/* Grooming cancellation success banner */}
                {groomingCancelBanner && (
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      border: '1px solid oklch(0.55 0.12 150 / 0.30)',
                      background: 'oklch(0.55 0.12 150 / 0.08)',
                      color: 'oklch(0.36 0.12 150)',
                      fontSize: '0.9rem',
                    }}
                    role="status"
                    data-testid="grooming-cancelled-banner"
                  >
                    {groomingCancelBanner.refundSource === 'account_credit'
                      ? `Appointment cancelled. ${formatMoney(groomingCancelBanner.refundMinor)} has been added to your account credit.`
                      : 'Appointment cancelled.'}
                  </div>
                )}

                {/* Refund submitted success banner */}
                {refundSubmittedBanner && (
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      border: '1px solid oklch(0.55 0.12 150 / 0.30)',
                      background: 'oklch(0.55 0.12 150 / 0.08)',
                      color: 'oklch(0.36 0.12 150)',
                      fontSize: '0.9rem',
                    }}
                    role="status"
                    data-testid="refund-submitted-banner"
                  >
                    Your refund request has been submitted. The kennel will review it shortly.
                  </div>
                )}

                {/* Daycare self-serve cancel CTA (sub-spec 04 Plan 5 M4) */}
                {data.type === 'daycare' &&
                  isDaycareCancellable(booking.status) &&
                  !showDaycareCancelDrawer &&
                  !daycareCancelBanner && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowDaycareCancelDrawer(true)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                          background: 'transparent',
                          color: 'var(--destructive, oklch(0.55 0.15 30))',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        data-testid="daycare-cancel-cta"
                      >
                        Cancel visit
                      </button>
                    </div>
                  )}

                {/* Daycare cancel drawer */}
                {showDaycareCancelDrawer && (
                  <CancelDaycareDrawer
                    bookingId={booking.id}
                    onSuccess={handleDaycareCancelSuccess}
                    onClose={() => setShowDaycareCancelDrawer(false)}
                  />
                )}

                {/* Grooming self-serve cancel CTA (sub-spec 05 Plan 5 M2). */}
                {data.type === 'grooming' &&
                  isGroomingCancellable(booking.status, data.appointment.startAt) &&
                  !showGroomingCancelDrawer &&
                  !groomingCancelBanner && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowGroomingCancelDrawer(true)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                          background: 'transparent',
                          color: 'var(--destructive, oklch(0.55 0.15 30))',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        data-testid="grooming-cancel-cta"
                      >
                        Cancel appointment
                      </button>
                    </div>
                  )}

                {/* Grooming cancel drawer */}
                {showGroomingCancelDrawer && (
                  <CancelGroomingDrawer
                    bookingId={booking.id}
                    onSuccess={handleGroomingCancelSuccess}
                    onClose={() => setShowGroomingCancelDrawer(false)}
                  />
                )}

                {/* Cancel & request refund CTA — boarding only now. Daycare +
                grooming have their own self-serve cancel drawers; this CTA
                remains as the fallback for boarding (which goes through staff
                review). */}
                {data.type !== 'daycare' &&
                  data.type !== 'grooming' &&
                  isCancellable(booking.status) &&
                  !showRefundDrawer &&
                  !refundSubmittedBanner && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowRefundDrawer(true)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          border: '1px solid var(--destructive, oklch(0.55 0.15 30))',
                          background: 'transparent',
                          color: 'var(--destructive, oklch(0.55 0.15 30))',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                        data-testid="cancel-refund-cta"
                      >
                        Cancel &amp; request refund
                      </button>
                    </div>
                  )}

                {/* Refund request drawer */}
                {showRefundDrawer && (
                  <RequestRefundDrawer
                    bookingId={booking.id}
                    onSuccess={handleRefundSuccess}
                    onClose={() => setShowRefundDrawer(false)}
                  />
                )}

                {/* Migration 0079 — "I'm on my way" CTA. Shown for any active
                    booking; the drawer lets the customer pick ETA + plate. */}
                {isCancellable(booking.status) && !showOnMyWayDrawer && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowOnMyWayDrawer(true)}
                      data-testid="on-my-way-cta"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '10px',
                        background: 'var(--primary, oklch(0.43 0.18 263))',
                        color: 'white',
                        border: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      I'm on my way
                    </button>
                  </div>
                )}
                {onMyWayBanner && (
                  <div
                    role="status"
                    data-testid="on-my-way-banner"
                    style={{
                      marginTop: '1rem',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      border: '1px solid oklch(0.55 0.12 150 / 0.30)',
                      background: 'oklch(0.55 0.12 150 / 0.08)',
                      color: 'oklch(0.36 0.12 150)',
                      fontSize: '0.9rem',
                    }}
                  >
                    The team has been notified. Drive safe!
                  </div>
                )}
                {showOnMyWayDrawer && (
                  <OnMyWayDrawer
                    bookingId={booking.id}
                    onSuccess={() => {
                      setShowOnMyWayDrawer(false);
                      setOnMyWayBanner(true);
                    }}
                    onClose={() => setShowOnMyWayDrawer(false)}
                  />
                )}

                {/* Self-serve reschedule CTA — only when booking is still
                    reschedulable (not past start, not cancelled, not
                    checked-in/out). */}
                {data.type !== 'service' &&
                  isCancellable(booking.status) &&
                  !showRescheduleDrawer &&
                  !rescheduledBanner && (
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowRescheduleDrawer(true)}
                        data-testid="reschedule-cta"
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          border: '1px solid var(--border, oklch(0.915 0.005 80))',
                          background: 'transparent',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        Reschedule
                      </button>
                    </div>
                  )}

                {rescheduledBanner && (
                  <div
                    style={{
                      marginTop: '1.5rem',
                      padding: '1rem 1.25rem',
                      borderRadius: '12px',
                      border: '1px solid oklch(0.55 0.12 150 / 0.30)',
                      background: 'oklch(0.55 0.12 150 / 0.08)',
                      color: 'oklch(0.36 0.12 150)',
                      fontSize: '0.9rem',
                    }}
                    role="status"
                    data-testid="reschedule-banner"
                  >
                    Booking rescheduled. You'll get a confirmation email shortly.
                  </div>
                )}

                {showRescheduleDrawer && (
                  <RescheduleDrawer
                    bookingId={booking.id}
                    bookingType={data.type}
                    onSuccess={() => {
                      setShowRescheduleDrawer(false);
                      setRescheduledBanner(true);
                      fetchBooking();
                    }}
                    onClose={() => setShowRescheduleDrawer(false)}
                  />
                )}

                {/* "Book this again" — past bookings (checked_out / completed). */}
                {(booking.status === 'checked_out' || booking.status === 'completed') && (
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/book?repeatBookingId=${booking.id}`)}
                      data-testid="book-again-cta"
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '10px',
                        background: 'var(--primary, oklch(0.43 0.18 263))',
                        color: 'white',
                        border: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Book this again
                    </button>
                  </div>
                )}

                {/* Pet report cards (migration 0093) — every card the kennel
                    has sent for this stay, newest first. */}
                {reportCards.length > 0 && (
                  <Section title="Report cards">
                    <ReportCardList cards={reportCards} petName={data.petName ?? null} />
                  </Section>
                )}

                {/* Live photo timeline — when the booking is checked-in,
                    fan-out subscribes via useStayRealtime which bumps the
                    realtimeNonce on BOOKING_PHOTO_POSTED events. */}
                {booking.status === 'checked_in' && (
                  <Section title="Photo timeline">
                    <PhotoTimeline
                      bookingId={booking.id}
                      scope="customer"
                      realtimeNonce={realtimeNonce}
                    />
                  </Section>
                )}

                <Section title="Messages">
                  <MessagesThread
                    bookingId={booking.id}
                    context="customer-account"
                    realtimeNonce={realtimeNonce}
                  />
                </Section>

                <Section title="Payment history">
                  {payments === null ? (
                    <p
                      style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                      }}
                    >
                      Loading…
                    </p>
                  ) : (
                    (() => {
                      const forThisBooking = payments.filter((p) => p.bookingId === booking.id);
                      if (forThisBooking.length === 0) {
                        return (
                          <p
                            style={{
                              margin: 0,
                              fontSize: '0.875rem',
                              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                            }}
                          >
                            No payments yet.
                          </p>
                        );
                      }
                      return forThisBooking.map((p) => {
                        const signed = p.direction === 'out' ? -p.amountMinor : p.amountMinor;
                        const formatted = formatMoney(Math.abs(signed), p.currency);
                        const display = p.direction === 'out' ? `-${formatted}` : formatted;
                        return (
                          <Row
                            key={p.id}
                            label={
                              <span>
                                <span style={{ textTransform: 'capitalize' }}>
                                  {p.kind.replace(/_/g, ' ')}
                                </span>
                                <span
                                  style={{
                                    marginLeft: '0.5rem',
                                    fontSize: '0.7rem',
                                    color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                                  }}
                                >
                                  {p.status}
                                </span>
                              </span>
                            }
                            value={
                              <span className="num" data-direction={p.direction}>
                                {display}
                              </span>
                            }
                          />
                        );
                      });
                    })()
                  )}
                </Section>
              </>
            );
          })()}
      </div>
    </main>
  );
}
